import { Module } from '../../types';

export const dspyModule6: Module = {
  id: 'dspy-module6',
  title: 'Building Real-World Applications',
  description:
    'Work through comprehensive examples of classification systems, question-answering applications, intelligent agents, and content generation pipelines.',
  duration: '6-8 hours',
  isUnderDevelopment: true,
  content: `# Module 6: Building Real-World Applications

## 🎯 Module Overview
This module brings together everything you've learned to build production-ready AI applications. You'll work through comprehensive examples of classification systems, question-answering applications, intelligent agents, and content generation pipelines. Each section includes complete, working code that demonstrates best practices and real-world considerations.

## 🎯 Learning Objectives
- Build robust classification systems with confidence scores and explanations
- Create sophisticated question-answering systems with citations and multi-hop reasoning
- Develop intelligent agents that can use tools and maintain conversation state
- Implement content generation pipelines for various use cases
- Apply optimization techniques to real-world applications
- Handle edge cases and error scenarios in production systems

---

## 6.1 Classification Systems

### Understanding Classification in DSPy

Classification is one of the most common AI tasks. DSPy makes it easy to build classifiers that not only categorize inputs but also provide explanations and confidence scores.

#### Building a Multi-Class Email Classifier

\`\`\`python
import dspy
from typing import List, Optional, Literal
from enum import Enum
from pydantic import BaseModel

class EmailType(str, Enum):
    ORDER_CONFIRMATION = "order_confirmation"
    SUPPORT_REQUEST = "support_request"
    MEETING_INVITATION = "meeting_invitation"
    NEWSLETTER = "newsletter"
    PROMOTIONAL = "promotional"
    INVOICE = "invoice"
    SHIPPING_NOTIFICATION = "shipping_notification"
    OTHER = "other"

class UrgencyLevel(str, Enum):
    LOW = "low"
    MEDIUM = "medium"
    HIGH = "high"
    CRITICAL = "critical"

class EmailClassification(dspy.Signature):
    """Classify emails by type and urgency with detailed reasoning."""
    
    email_subject: str = dspy.InputField(desc="The subject line of the email")
    email_body: str = dspy.InputField(desc="The main content of the email")
    sender: str = dspy.InputField(desc="Email sender information")
    
    email_type: EmailType = dspy.OutputField(desc="The classified type of email")
    urgency: UrgencyLevel = dspy.OutputField(desc="The urgency level of the email")
    confidence: float = dspy.OutputField(desc="Confidence score between 0 and 1")
    reasoning: str = dspy.OutputField(desc="Detailed explanation of the classification")
    key_indicators: List[str] = dspy.OutputField(desc="Key phrases that influenced the classification")

class EmailClassifier(dspy.Module):
    """Production-ready email classifier with validation and error handling."""
    
    def __init__(self):
        super().__init__()
        self.classifier = dspy.ChainOfThought(EmailClassification)
        
    def forward(self, email_subject: str, email_body: str, sender: str = ""):
        # Input validation
        if not email_subject and not email_body:
            raise ValueError("Email must have either subject or body")
            
        # Truncate very long emails
        max_length = 2000
        if len(email_body) > max_length:
            email_body = email_body[:max_length] + "... [truncated]"
            
        try:
            result = self.classifier(
                email_subject=email_subject,
                email_body=email_body,
                sender=sender
            )
            
            # Validate confidence score
            if not 0 <= result.confidence <= 1:
                result.confidence = max(0, min(1, result.confidence))
                
            return result
            
        except Exception as e:
            # Fallback classification
            return dspy.Prediction(
                email_type=EmailType.OTHER,
                urgency=UrgencyLevel.MEDIUM,
                confidence=0.0,
                reasoning=f"Classification failed: {str(e)}",
                key_indicators=[]
            )
\`\`\`

#### Multi-Label Classification for Topics

\`\`\`python
class TopicClassification(dspy.Signature):
    """Classify text into multiple relevant topics."""
    
    text: str = dspy.InputField(desc="Text to classify")
    available_topics: List[str] = dspy.InputField(desc="List of possible topics")
    
    selected_topics: List[str] = dspy.OutputField(desc="All relevant topics")
    primary_topic: str = dspy.OutputField(desc="The most relevant topic")
    topic_scores: dict = dspy.OutputField(desc="Confidence score for each selected topic")
    reasoning: str = dspy.OutputField(desc="Explanation of topic selection")

class MultiLabelClassifier(dspy.Module):
    def __init__(self, topics: List[str], threshold: float = 0.5):
        super().__init__()
        self.topics = topics
        self.threshold = threshold
        self.classifier = dspy.ChainOfThought(TopicClassification)
        
    def forward(self, text: str):
        result = self.classifier(
            text=text,
            available_topics=self.topics
        )
        
        # Filter topics by threshold
        filtered_scores = {
            topic: score 
            for topic, score in result.topic_scores.items() 
            if score >= self.threshold
        }
        
        return dspy.Prediction(
            selected_topics=list(filtered_scores.keys()),
            primary_topic=result.primary_topic,
            topic_scores=filtered_scores,
            reasoning=result.reasoning
        )

# Example usage
topics = [
    "Technology", "Business", "Science", "Health", 
    "Politics", "Sports", "Entertainment", "Education"
]

classifier = MultiLabelClassifier(topics=topics, threshold=0.6)

result = classifier(
    text="Apple announced its new AI-powered health monitoring features in the latest iPhone, which uses machine learning to detect potential health issues early."
)

print(f"Primary topic: {result.primary_topic}")
print(f"All topics: {result.selected_topics}")
print(f"Scores: {result.topic_scores}")
\`\`\`

#### Building a Sentiment Analyzer with Aspects

\`\`\`python
class AspectSentiment(BaseModel):
    aspect: str
    sentiment: Literal["positive", "negative", "neutral"]
    confidence: float
    evidence: str

class AspectBasedSentiment(dspy.Signature):
    """Analyze sentiment for different aspects of a product or service."""
    
    text: str = dspy.InputField(desc="Review or feedback text")
    aspects_to_analyze: List[str] = dspy.InputField(desc="Specific aspects to analyze")
    
    overall_sentiment: Literal["positive", "negative", "neutral", "mixed"] = dspy.OutputField()
    aspect_sentiments: List[AspectSentiment] = dspy.OutputField()
    summary: str = dspy.OutputField(desc="Brief summary of the sentiment analysis")

class SentimentAnalyzer(dspy.Module):
    def __init__(self, default_aspects: List[str] = None):
        super().__init__()
        self.default_aspects = default_aspects or [
            "quality", "price", "service", "delivery", "features"
        ]
        self.analyzer = dspy.ChainOfThought(AspectBasedSentiment)
        
    def forward(self, text: str, aspects: List[str] = None):
        aspects_to_analyze = aspects or self.default_aspects
        
        result = self.analyzer(
            text=text,
            aspects_to_analyze=aspects_to_analyze
        )
        
        # Post-process to ensure all aspects are covered
        analyzed_aspects = {a.aspect for a in result.aspect_sentiments}
        for aspect in aspects_to_analyze:
            if aspect not in analyzed_aspects:
                # Add neutral sentiment for unmentioned aspects
                result.aspect_sentiments.append(
                    AspectSentiment(
                        aspect=aspect,
                        sentiment="neutral",
                        confidence=0.5,
                        evidence="Not mentioned in the text"
                    )
                )
                
        return result
\`\`\`

### Optimizing Classification Systems

\`\`\`python
# Create training data
trainset = [
    dspy.Example(
        email_subject="Order #12345 Shipped!",
        email_body="Your order has been shipped and will arrive in 2-3 days.",
        sender="orders@shop.com",
        email_type=EmailType.SHIPPING_NOTIFICATION,
        urgency=UrgencyLevel.LOW
    ).with_inputs("email_subject", "email_body", "sender"),
    # Add more examples...
]

# Define metric
def classification_metric(gold, pred, trace=None):
    type_correct = gold.email_type == pred.email_type
    urgency_correct = gold.urgency == pred.urgency
    has_reasoning = len(pred.reasoning) > 20
    
    # Weighted score
    score = (0.5 * type_correct + 0.3 * urgency_correct + 0.2 * has_reasoning)
    return score

# Optimize
from dspy.teleprompt import BootstrapFewShot

optimizer = BootstrapFewShot(metric=classification_metric, max_bootstrapped_demos=3)
optimized_classifier = optimizer.compile(
    EmailClassifier(),
    trainset=trainset
)
\`\`\`

---

## 6.2 Question-Answering Systems

### Simple Question Answering

\`\`\`python
class SimpleQA(dspy.Module):
    """Basic question-answering system."""
    
    def __init__(self):
        super().__init__()
        self.answer = dspy.ChainOfThought("question -> answer")
        
    def forward(self, question: str):
        return self.answer(question=question)

# Usage
qa = SimpleQA()
result = qa(question="What is the capital of France?")
print(result.answer)  # Paris
\`\`\`

### Multi-Hop Question Answering

\`\`\`python
class MultiHopQA(dspy.Module):
    """Question answering that performs multiple reasoning steps."""
    
    def __init__(self, max_hops: int = 3):
        super().__init__()
        self.max_hops = max_hops
        
        # Decompose question into sub-questions
        self.decompose = dspy.ChainOfThought(
            "question -> sub_questions: list[str], reasoning: str"
        )
        
        # Answer individual questions
        self.answer_sub = dspy.ChainOfThought(
            "sub_question, context -> answer, confidence: float"
        )
        
        # Synthesize final answer
        self.synthesize = dspy.ChainOfThought(
            "original_question, sub_answers: list[str] -> final_answer, reasoning_path: str"
        )
        
    def forward(self, question: str):
        # Step 1: Decompose the question
        decomposition = self.decompose(question=question)
        sub_questions = decomposition.sub_questions[:self.max_hops]
        
        # Step 2: Answer each sub-question
        sub_answers = []
        context = ""
        
        for sub_q in sub_questions:
            result = self.answer_sub(
                sub_question=sub_q,
                context=context
            )
            sub_answers.append(result.answer)
            # Build context for next question
            context += f"Q: {sub_q}\\nA: {result.answer}\\n"
            
        # Step 3: Synthesize final answer
        final_result = self.synthesize(
            original_question=question,
            sub_answers=sub_answers
        )
        
        return dspy.Prediction(
            answer=final_result.final_answer,
            reasoning_path=final_result.reasoning_path,
            sub_questions=sub_questions,
            sub_answers=sub_answers
        )

# Example usage
multi_qa = MultiHopQA(max_hops=3)
result = multi_qa(
    question="What is the population of the capital city of Japan?"
)
print(f"Answer: {result.answer}")
print(f"Reasoning: {result.reasoning_path}")
\`\`\`

### RAG-Based Question Answering

\`\`\`python
class RAGSystem(dspy.Module):
    """Retrieval-Augmented Generation for question answering."""
    
    def __init__(self, retriever, rerank: bool = True):
        super().__init__()
        self.retriever = retriever
        self.rerank = rerank
        
        # Query generation
        self.generate_queries = dspy.ChainOfThought(
            "question -> search_queries: list[str]"
        )
        
        # Reranking module
        if self.rerank:
            self.reranker = dspy.ChainOfThought(
                "question, passage -> relevance_score: float, reasoning: str"
            )
        
        # Answer generation with citations
        self.answer = dspy.ChainOfThought(
            """question, context: list[str] -> 
            answer: str, 
            citations: list[int],
            confidence: float,
            answer_type: str"""
        )
        
    def forward(self, question: str, num_passages: int = 5):
        # Generate search queries
        queries = self.generate_queries(question=question).search_queries
        
        # Retrieve passages
        all_passages = []
        passage_metadata = []
        
        for query in queries[:3]:  # Limit to 3 queries
            results = self.retriever(query, k=num_passages)
            for i, passage in enumerate(results.passages):
                all_passages.append(passage)
                passage_metadata.append({
                    'query': query,
                    'rank': i,
                    'passage_id': len(all_passages) - 1
                })
        
        # Rerank if enabled
        if self.rerank and len(all_passages) > num_passages:
            scored_passages = []
            for i, passage in enumerate(all_passages):
                score_result = self.reranker(
                    question=question,
                    passage=passage
                )
                scored_passages.append({
                    'passage': passage,
                    'score': score_result.relevance_score,
                    'id': i
                })
            
            # Sort by score and take top passages
            scored_passages.sort(key=lambda x: x['score'], reverse=True)
            selected_passages = scored_passages[:num_passages]
            
            # Prepare context and track IDs for citations
            context = [p['passage'] for p in selected_passages]
            passage_ids = [p['id'] for p in selected_passages]
        else:
            context = all_passages[:num_passages]
            passage_ids = list(range(len(context)))
        
        # Generate answer
        result = self.answer(
            question=question,
            context=context
        )
        
        # Map citations to original passage IDs
        mapped_citations = [passage_ids[c] for c in result.citations if c < len(passage_ids)]
        
        return dspy.Prediction(
            answer=result.answer,
            citations=mapped_citations,
            confidence=result.confidence,
            answer_type=result.answer_type,
            context_used=context
        )
\`\`\`

### Question Answering with Fact Verification

\`\`\`python
class VerifiedQA(dspy.Module):
    """QA system that verifies its answers against sources."""
    
    def __init__(self, retriever):
        super().__init__()
        self.retriever = retriever
        
        # Initial answer generation
        self.initial_answer = dspy.ChainOfThought(
            "question -> answer, key_claims: list[str]"
        )
        
        # Fact verification
        self.verify_claim = dspy.ChainOfThought(
            """claim, evidence: str -> 
            is_supported: bool, 
            confidence: float,
            explanation: str"""
        )
        
        # Answer refinement
        self.refine_answer = dspy.ChainOfThought(
            """question, 
            initial_answer: str,
            verification_results: list[dict] ->
            refined_answer: str,
            factual_confidence: float"""
        )
        
    def forward(self, question: str):
        # Generate initial answer
        initial = self.initial_answer(question=question)
        
        # Verify each claim
        verification_results = []
        for claim in initial.key_claims:
            # Retrieve evidence for the claim
            evidence_results = self.retriever(claim, k=3)
            evidence = "\\n".join(evidence_results.passages)
            
            # Verify the claim
            verification = self.verify_claim(
                claim=claim,
                evidence=evidence
            )
            
            verification_results.append({
                'claim': claim,
                'is_supported': verification.is_supported,
                'confidence': verification.confidence,
                'explanation': verification.explanation
            })
        
        # Refine answer based on verification
        refined = self.refine_answer(
            question=question,
            initial_answer=initial.answer,
            verification_results=verification_results
        )
        
        return dspy.Prediction(
            answer=refined.refined_answer,
            factual_confidence=refined.factual_confidence,
            initial_answer=initial.answer,
            key_claims=initial.key_claims,
            verification_results=verification_results
        )
\`\`\`

---

## 6.3 Agent Systems

### Basic ReAct Agent

\`\`\`python
class CustomerServiceAgent(dspy.Module):
    """ReAct agent for customer service tasks."""
    
    def __init__(self):
        super().__init__()
        
        # Define available tools
        self.tools = [
            self.check_order_status,
            self.process_refund,
            self.update_shipping_address,
            self.search_faq,
            self.escalate_to_human
        ]
        
        # Initialize ReAct
        self.agent = dspy.ReAct(
            signature="customer_query -> response",
            tools=self.tools,
            max_iters=5
        )
        
    def check_order_status(self, order_id: str) -> str:
        """Check the status of an order."""
        # Simulated database lookup
        statuses = {
            "12345": "Shipped - Expected delivery: Tomorrow",
            "67890": "Processing - Will ship within 24 hours",
        }
        return statuses.get(order_id, "Order not found")
        
    def process_refund(self, order_id: str, reason: str) -> str:
        """Process a refund request."""
        return f"Refund initiated for order {order_id}. Reason: {reason}. Expected in 3-5 business days."
        
    def update_shipping_address(self, order_id: str, new_address: str) -> str:
        """Update shipping address for an order."""
        return f"Shipping address updated for order {order_id} to: {new_address}"
        
    def search_faq(self, query: str) -> str:
        """Search FAQ database."""
        faqs = {
            "shipping": "Standard shipping takes 3-5 business days. Express shipping takes 1-2 days.",
            "returns": "Items can be returned within 30 days for a full refund.",
            "warranty": "All products come with a 1-year manufacturer warranty."
        }
        
        for key, value in faqs.items():
            if key in query.lower():
                return value
        return "No FAQ found for your query."
        
    def escalate_to_human(self, issue: str) -> str:
        """Escalate complex issues to human support."""
        return f"I've escalated your issue to a human agent. Issue: {issue}. You'll receive a response within 2 hours."
        
    def forward(self, customer_query: str):
        return self.agent(customer_query=customer_query)

# Usage
agent = CustomerServiceAgent()
response = agent(customer_query="I need to check on order 12345 and update the shipping address")
print(response.response)
\`\`\`

### Memory-Enhanced Agent

\`\`\`python
class MemoryAgent(dspy.Module):
    """Agent with conversation memory and context awareness."""
    
    def __init__(self, memory_store=None):
        super().__init__()
        self.memory_store = memory_store or {}
        self.conversation_history = []
        
        # Memory operations
        self.store_memory = self.create_memory_tool()
        self.recall_memory = self.create_recall_tool()
        
        # Core reasoning with memory
        self.reason_with_memory = dspy.ChainOfThought(
            """query: str,
            relevant_memories: list[str],
            conversation_history: list[str] ->
            response: str,
            memories_to_store: list[str],
            confidence: float"""
        )
        
    def create_memory_tool(self):
        def store_memory(key: str, value: str, category: str = "general") -> str:
            """Store information in memory."""
            if key not in self.memory_store:
                self.memory_store[key] = []
            
            memory_entry = {
                'value': value,
                'category': category,
                'timestamp': dspy.utils.get_timestamp(),
                'confidence': 1.0
            }
            self.memory_store[key].append(memory_entry)
            return f"Stored: {value} under key: {key}"
        
        return store_memory
        
    def create_recall_tool(self):
        def recall_memory(key: str, category: str = None) -> str:
            """Recall information from memory."""
            if key not in self.memory_store:
                return f"No memories found for key: {key}"
                
            memories = self.memory_store[key]
            if category:
                memories = [m for m in memories if m['category'] == category]
                
            if not memories:
                return f"No memories found for key: {key} with category: {category}"
                
            # Return most recent memories
            memory_texts = [m['value'] for m in memories[-5:]]
            return f"Memories for {key}: " + "; ".join(memory_texts)
        
        return recall_memory
        
    def forward(self, query: str, user_id: str = "default"):
        # Search for relevant memories
        memory_keys = list(self.memory_store.keys())
        relevant_memories = []
        
        for key in memory_keys:
            if any(word in query.lower() for word in key.lower().split()):
                memory_content = self.recall_memory(key)
                relevant_memories.append(memory_content)
        
        # Get conversation context
        recent_history = self.conversation_history[-5:]
        
        # Generate response with memory context
        result = self.reason_with_memory(
            query=query,
            relevant_memories=relevant_memories,
            conversation_history=recent_history
        )
        
        # Store new memories
        for memory in result.memories_to_store:
            # Extract key-value pairs from memory
            if ":" in memory:
                key, value = memory.split(":", 1)
                self.store_memory(key.strip(), value.strip())
        
        # Update conversation history
        self.conversation_history.append(f"User: {query}")
        self.conversation_history.append(f"Agent: {result.response}")
        
        return dspy.Prediction(
            response=result.response,
            confidence=result.confidence,
            memories_used=relevant_memories,
            conversation_length=len(self.conversation_history)
        )
\`\`\`

### Custom Tool-Using Agent

\`\`\`python
class ResearchAgent(dspy.Module):
    """Advanced agent for research tasks with custom tools."""
    
    def __init__(self, web_search_api, calculator, code_executor):
        super().__init__()
        self.web_search_api = web_search_api
        self.calculator = calculator
        self.code_executor = code_executor
        
        # Plan research approach
        self.planner = dspy.ChainOfThought(
            """research_question: str ->
            research_plan: list[str],
            required_tools: list[str],
            expected_output_format: str"""
        )
        
        # Execute research with tools
        self.executor = dspy.ReAct(
            signature="""research_question: str,
                      research_plan: list[str] ->
                      findings: str,
                      evidence: list[str],
                      confidence: float""",
            tools=[
                self.search_web,
                self.calculate,
                self.run_analysis_code,
                self.summarize_findings
            ],
            max_iters=10
        )
        
        # Validate and refine results
        self.validator = dspy.ChainOfThought(
            """findings: str,
            evidence: list[str],
            original_question: str ->
            is_complete: bool,
            missing_aspects: list[str],
            quality_score: float"""
        )
        
    def search_web(self, query: str, num_results: int = 5) -> str:
        """Search the web for information."""
        try:
            results = self.web_search_api.search(query, num_results)
            return f"Found {len(results)} results:\\n" + "\\n".join(
                [f"- {r.title}: {r.snippet}" for r in results]
            )
        except Exception as e:
            return f"Search error: {str(e)}"
            
    def calculate(self, expression: str) -> str:
        """Perform calculations."""
        try:
            result = self.calculator.evaluate(expression)
            return f"Calculation result: {expression} = {result}"
        except Exception as e:
            return f"Calculation error: {str(e)}"
            
    def run_analysis_code(self, code: str, language: str = "python") -> str:
        """Execute analysis code safely."""
        try:
            result = self.code_executor.run(code, language, timeout=10)
            return f"Code output:\\n{result.output}"
        except Exception as e:
            return f"Code execution error: {str(e)}"
            
    def summarize_findings(self, text: str, max_length: int = 200) -> str:
        """Summarize research findings."""
        summarizer = dspy.ChainOfThought("text, max_length: int -> summary")
        result = summarizer(text=text, max_length=max_length)
        return result.summary
        
    def forward(self, research_question: str):
        # Step 1: Plan the research
        plan = self.planner(research_question=research_question)
        
        # Step 2: Execute research plan
        research_result = self.executor(
            research_question=research_question,
            research_plan=plan.research_plan
        )
        
        # Step 3: Validate completeness
        validation = self.validator(
            findings=research_result.findings,
            evidence=research_result.evidence,
            original_question=research_question
        )
        
        # Step 4: Refine if needed
        if not validation.is_complete and validation.missing_aspects:
            # Do another round of research for missing aspects
            additional_question = f"Find information about: {', '.join(validation.missing_aspects)}"
            additional_result = self.executor(
                research_question=additional_question,
                research_plan=["Search for missing information", "Synthesize with previous findings"]
            )
            
            # Combine findings
            combined_findings = f"{research_result.findings}\\n\\nAdditional findings:\\n{additional_result.findings}"
            combined_evidence = research_result.evidence + additional_result.evidence
        else:
            combined_findings = research_result.findings
            combined_evidence = research_result.evidence
            
        return dspy.Prediction(
            findings=combined_findings,
            evidence=combined_evidence,
            confidence=research_result.confidence,
            quality_score=validation.quality_score,
            research_plan=plan.research_plan,
            output_format=plan.expected_output_format
        )
\`\`\`

---

## 6.4 Content Generation

### Article Writing Pipeline

\`\`\`python
class ArticleWriter(dspy.Module):
    """Complete article writing system with research and citations."""
    
    def __init__(self, retriever=None):
        super().__init__()
        self.retriever = retriever
        
        # Topic analysis and outline
        self.analyze_topic = dspy.ChainOfThought(
            """topic: str,
            target_audience: str,
            article_type: str ->
            key_points: list[str],
            outline: list[str],
            research_queries: list[str]"""
        )
        
        # Section writer
        self.write_section = dspy.ChainOfThought(
            """section_title: str,
            section_outline: str,
            research_context: str,
            previous_sections: str ->
            section_content: str,
            citations_used: list[str]"""
        )
        
        # Introduction writer
        self.write_intro = dspy.ChainOfThought(
            """topic: str,
            outline: list[str],
            key_points: list[str] ->
            introduction: str,
            hook: str"""
        )
        
        # Conclusion writer
        self.write_conclusion = dspy.ChainOfThought(
            """topic: str,
            main_content: str,
            key_points: list[str] ->
            conclusion: str,
            call_to_action: str"""
        )
        
        # Title generator
        self.generate_title = dspy.ChainOfThought(
            """topic: str,
            content_summary: str,
            target_audience: str ->
            title: str,
            subtitle: str,
            seo_keywords: list[str]"""
        )
        
    def research_topic(self, queries: List[str]) -> Dict[str, List[str]]:
        """Conduct research for the article."""
        research_results = {}
        
        if self.retriever:
            for query in queries:
                results = self.retriever(query, k=5)
                research_results[query] = results.passages
        else:
            # Fallback to general knowledge
            for query in queries:
                research_results[query] = [
                    f"General information about {query}"
                ]
                
        return research_results
        
    def forward(self, 
                topic: str,
                target_audience: str = "general readers",
                article_type: str = "informative",
                word_count: int = 1000):
        
        # Step 1: Analyze topic and create outline
        analysis = self.analyze_topic(
            topic=topic,
            target_audience=target_audience,
            article_type=article_type
        )
        
        # Step 2: Conduct research
        research_results = self.research_topic(analysis.research_queries)
        research_context = "\\n\\n".join([
            f"Research on '{query}':\\n" + "\\n".join(passages)
            for query, passages in research_results.items()
        ])
        
        # Step 3: Write introduction
        intro_result = self.write_intro(
            topic=topic,
            outline=analysis.outline,
            key_points=analysis.key_points
        )
        
        # Step 4: Write main sections
        sections = []
        previous_content = intro_result.introduction
        all_citations = []
        
        for section_outline in analysis.outline:
            section_result = self.write_section(
                section_title=section_outline,
                section_outline=section_outline,
                research_context=research_context,
                previous_sections=previous_content
            )
            
            sections.append({
                'title': section_outline,
                'content': section_result.section_content,
                'citations': section_result.citations_used
            })
            
            previous_content += f"\\n\\n{section_result.section_content}"
            all_citations.extend(section_result.citations_used)
        
        # Step 5: Write conclusion
        conclusion_result = self.write_conclusion(
            topic=topic,
            main_content=previous_content,
            key_points=analysis.key_points
        )
        
        # Step 6: Generate title and metadata
        content_summary = f"{intro_result.introduction[:200]}..."
        title_result = self.generate_title(
            topic=topic,
            content_summary=content_summary,
            target_audience=target_audience
        )
        
        # Compile final article
        article_parts = [
            f"# {title_result.title}",
            f"*{title_result.subtitle}*" if title_result.subtitle else "",
            "",
            intro_result.introduction,
            ""
        ]
        
        for section in sections:
            article_parts.extend([
                f"## {section['title']}",
                "",
                section['content'],
                ""
            ])
            
        article_parts.extend([
            conclusion_result.conclusion,
            "",
            conclusion_result.call_to_action if conclusion_result.call_to_action else ""
        ])
        
        full_article = "\\n".join(article_parts).strip()
        
        # Calculate word count
        actual_word_count = len(full_article.split())
        
        return dspy.Prediction(
            title=title_result.title,
            subtitle=title_result.subtitle,
            article=full_article,
            word_count=actual_word_count,
            seo_keywords=title_result.seo_keywords,
            outline=analysis.outline,
            citations=list(set(all_citations)),
            sections=sections
        )
\`\`\`

### Summarization System

\`\`\`python
class AdaptiveSummarizer(dspy.Module):
    """Summarization system that adapts to different content types and lengths."""
    
    def __init__(self):
        super().__init__()
        
        # Content analyzer
        self.analyze_content = dspy.ChainOfThought(
            """text: str ->
            content_type: str,
            key_topics: list[str],
            complexity_level: str,
            suggested_summary_length: int"""
        )
        
        # Extractive summarizer
        self.extract_key_sentences = dspy.ChainOfThought(
            """text: str,
            num_sentences: int ->
            key_sentences: list[str],
            importance_scores: list[float]"""
        )
        
        # Abstractive summarizer
        self.generate_summary = dspy.ChainOfThought(
            """text: str,
            key_sentences: list[str],
            target_length: int,
            style: str ->
            summary: str,
            main_points: list[str]"""
        )
        
        # Quality checker
        self.check_quality = dspy.ChainOfThought(
            """original_text: str,
            summary: str ->
            completeness_score: float,
            accuracy_score: float,
            readability_score: float,
            issues: list[str]"""
        )
        
    def forward(self, 
                text: str,
                target_length: int = None,
                summary_style: str = "informative"):
        
        # Step 1: Analyze content
        analysis = self.analyze_content(text=text)
        
        # Determine target length if not specified
        if target_length is None:
            target_length = analysis.suggested_summary_length
            
        # Step 2: Extract key sentences
        num_sentences = max(3, target_length // 20)  # Rough estimate
        extraction = self.extract_key_sentences(
            text=text,
            num_sentences=num_sentences
        )
        
        # Step 3: Generate summary
        summary_result = self.generate_summary(
            text=text,
            key_sentences=extraction.key_sentences,
            target_length=target_length,
            style=summary_style
        )
        
        # Step 4: Quality check
        quality = self.check_quality(
            original_text=text,
            summary=summary_result.summary
        )
        
        # Step 5: Refine if needed
        if quality.completeness_score < 0.7 or quality.accuracy_score < 0.8:
            # Regenerate with feedback
            refinement_prompt = f"""
            Original summary has issues: {', '.join(quality.issues)}
            Please improve the summary addressing these concerns.
            """
            
            refined_summary = self.generate_summary(
                text=text + f"\\n\\nRefinement notes: {refinement_prompt}",
                key_sentences=extraction.key_sentences,
                target_length=target_length,
                style=summary_style
            )
            
            final_summary = refined_summary.summary
            main_points = refined_summary.main_points
        else:
            final_summary = summary_result.summary
            main_points = summary_result.main_points
            
        return dspy.Prediction(
            summary=final_summary,
            main_points=main_points,
            content_type=analysis.content_type,
            key_topics=analysis.key_topics,
            word_count=len(final_summary.split()),
            quality_scores={
                'completeness': quality.completeness_score,
                'accuracy': quality.accuracy_score,
                'readability': quality.readability_score
            }
        )
\`\`\`

### Report Generation

\`\`\`python
class ReportGenerator(dspy.Module):
    """Generate structured reports with data analysis and visualizations."""
    
    def __init__(self):
        super().__init__()
        
        # Data analyzer
        self.analyze_data = dspy.ChainOfThought(
            """data_description: str,
            report_purpose: str ->
            key_findings: list[str],
            data_insights: list[str],
            recommended_sections: list[str]"""
        )
        
        # Executive summary writer
        self.write_executive_summary = dspy.ChainOfThought(
            """report_title: str,
            key_findings: list[str],
            recommendations: list[str] ->
            executive_summary: str,
            key_takeaways: list[str]"""
        )
        
        # Data interpretation
        self.interpret_data = dspy.ChainOfThought(
            """data_point: str,
            context: str ->
            interpretation: str,
            implications: list[str],
            visualization_suggestion: str"""
        )
        
        # Recommendation generator
        self.generate_recommendations = dspy.ChainOfThought(
            """findings: list[str],
            data_insights: list[str],
            report_purpose: str ->
            recommendations: list[str],
            action_items: list[str],
            priority_levels: list[str]"""
        )
        
    def forward(self,
                report_title: str,
                data_description: str,
                report_purpose: str,
                data_points: List[Dict[str, Any]] = None):
        
        # Step 1: Analyze data and structure
        analysis = self.analyze_data(
            data_description=data_description,
            report_purpose=report_purpose
        )
        
        # Step 2: Process data points
        interpreted_data = []
        if data_points:
            for point in data_points:
                interpretation = self.interpret_data(
                    data_point=str(point),
                    context=report_purpose
                )
                interpreted_data.append({
                    'original': point,
                    'interpretation': interpretation.interpretation,
                    'implications': interpretation.implications,
                    'visualization': interpretation.visualization_suggestion
                })
        
        # Step 3: Generate recommendations
        all_findings = analysis.key_findings + analysis.data_insights
        recommendations = self.generate_recommendations(
            findings=all_findings,
            data_insights=analysis.data_insights,
            report_purpose=report_purpose
        )
        
        # Step 4: Write executive summary
        exec_summary = self.write_executive_summary(
            report_title=report_title,
            key_findings=analysis.key_findings,
            recommendations=recommendations.recommendations
        )
        
        # Step 5: Compile report structure
        report_structure = {
            'title': report_title,
            'executive_summary': exec_summary.executive_summary,
            'key_takeaways': exec_summary.key_takeaways,
            'sections': [],
            'recommendations': recommendations.recommendations,
            'action_items': recommendations.action_items
        }
        
        # Add main sections
        for section_name in analysis.recommended_sections:
            section_content = self._generate_section_content(
                section_name,
                analysis.key_findings,
                interpreted_data
            )
            report_structure['sections'].append({
                'title': section_name,
                'content': section_content
            })
        
        # Format final report
        report_text = self._format_report(report_structure)
        
        return dspy.Prediction(
            report=report_text,
            structure=report_structure,
            word_count=len(report_text.split()),
            key_findings=analysis.key_findings,
            recommendations=recommendations.recommendations,
            priority_actions=list(zip(
                recommendations.action_items,
                recommendations.priority_levels
            ))
        )
        
    def _generate_section_content(self, section_name, findings, data):
        """Generate content for a specific section."""
        section_writer = dspy.ChainOfThought(
            """section_name: str,
            relevant_findings: list[str],
            relevant_data: list[dict] ->
            section_content: str"""
        )
        
        result = section_writer(
            section_name=section_name,
            relevant_findings=findings,
            relevant_data=data
        )
        
        return result.section_content
        
    def _format_report(self, structure):
        """Format the report structure into final text."""
        lines = [
            f"# {structure['title']}",
            "",
            "## Executive Summary",
            structure['executive_summary'],
            "",
            "### Key Takeaways",
            *[f"- {takeaway}" for takeaway in structure['key_takeaways']],
            ""
        ]
        
        # Add main sections
        for section in structure['sections']:
            lines.extend([
                f"## {section['title']}",
                section['content'],
                ""
            ])
        
        # Add recommendations
        lines.extend([
            "## Recommendations",
            *[f"{i+1}. {rec}" for i, rec in enumerate(structure['recommendations'])],
            "",
            "## Action Items",
            *[f"- {item}" for item in structure['action_items']],
            ""
        ])
        
        return "\\n".join(lines)
\`\`\`

### Creative Writing Assistant

\`\`\`python
class CreativeWriter(dspy.Module):
    """AI assistant for creative writing tasks."""
    
    def __init__(self):
        super().__init__()
        
        # Story planner
        self.plan_story = dspy.ChainOfThought(
            """genre: str,
            prompt: str,
            target_length: str ->
            plot_outline: list[str],
            characters: list[dict],
            themes: list[str],
            setting: str"""
        )
        
        # Character developer
        self.develop_character = dspy.ChainOfThought(
            """character_name: str,
            role: str,
            story_context: str ->
            personality: str,
            backstory: str,
            motivations: list[str],
            character_arc: str"""
        )
        
        # Scene writer
        self.write_scene = dspy.ChainOfThought(
            """scene_description: str,
            characters_involved: list[str],
            previous_context: str,
            tone: str ->
            scene_content: str,
            dialogue: list[str],
            scene_transition: str"""
        )
        
        # Style adapter
        self.adapt_style = dspy.ChainOfThought(
            """text: str,
            target_style: str,
            style_examples: str ->
            adapted_text: str,
            style_elements_used: list[str]"""
        )
        
    def forward(self,
                prompt: str,
                genre: str = "general fiction",
                target_length: str = "short story",
                writing_style: str = "contemporary"):
        
        # Step 1: Plan the story
        story_plan = self.plan_story(
            genre=genre,
            prompt=prompt,
            target_length=target_length
        )
        
        # Step 2: Develop characters
        developed_characters = []
        for char in story_plan.characters:
            char_development = self.develop_character(
                character_name=char.get('name', 'Unknown'),
                role=char.get('role', 'supporting'),
                story_context=prompt
            )
            developed_characters.append({
                'name': char.get('name'),
                'details': char_development
            })
        
        # Step 3: Write scenes
        scenes = []
        previous_context = f"Setting: {story_plan.setting}\\n"
        
        for scene_outline in story_plan.plot_outline:
            # Determine which characters are in this scene
            characters_in_scene = [c['name'] for c in developed_characters[:2]]  # Simplified
            
            scene_result = self.write_scene(
                scene_description=scene_outline,
                characters_involved=characters_in_scene,
                previous_context=previous_context,
                tone=genre
            )
            
            scenes.append({
                'outline': scene_outline,
                'content': scene_result.scene_content,
                'dialogue': scene_result.dialogue
            })
            
            previous_context += f"\\n{scene_result.scene_content}"
        
        # Step 4: Compile and style the story
        full_story = "\\n\\n".join([scene['content'] for scene in scenes])
        
        # Apply style adaptation if needed
        if writing_style != "contemporary":
            style_result = self.adapt_style(
                text=full_story,
                target_style=writing_style,
                style_examples=f"Write in a {writing_style} style"
            )
            final_story = style_result.adapted_text
            style_elements = style_result.style_elements_used
        else:
            final_story = full_story
            style_elements = ["contemporary prose", "modern dialogue"]
        
        return dspy.Prediction(
            story=final_story,
            title=self._generate_title(prompt, genre),
            plot_outline=story_plan.plot_outline,
            characters=developed_characters,
            themes=story_plan.themes,
            setting=story_plan.setting,
            word_count=len(final_story.split()),
            style_elements=style_elements,
            scenes=scenes
        )
        
    def _generate_title(self, prompt, genre):
        """Generate a title for the story."""
        title_gen = dspy.Predict("prompt, genre -> title")
        result = title_gen(prompt=prompt, genre=genre)
        return result.title
\`\`\`

---

## Module 6 Summary

### Key Applications Built

✅ **Classification Systems**
- Multi-class email classifier with confidence scores
- Multi-label topic classification
- Aspect-based sentiment analysis
- Optimization strategies for classifiers

✅ **Question-Answering Systems**
- Simple QA with Chain of Thought
- Multi-hop reasoning systems
- RAG with citation tracking
- Fact verification systems

✅ **Agent Systems**
- Customer service ReAct agents
- Memory-enhanced conversational agents
- Research agents with custom tools
- State management patterns

✅ **Content Generation**
- Complete article writing pipelines
- Adaptive summarization systems
- Structured report generation
- Creative writing assistance

### Best Practices Applied

1. **Error Handling**: All systems include try-except blocks and fallback behavior
2. **Input Validation**: Check and sanitize inputs before processing
3. **Modular Design**: Each component is reusable and testable
4. **Optimization Ready**: Structured for easy optimization with DSPy teleprompters
5. **Production Considerations**: Rate limiting, caching, and performance optimization

### Quick Implementation Guide

\`\`\`python
# 1. Choose your application type
from dspy_applications import (
    EmailClassifier,
    RAGSystem, 
    CustomerServiceAgent,
    ArticleWriter
)

# 2. Configure DSPy
import dspy
lm = dspy.LM('openai/gpt-4o-mini')
dspy.configure(lm=lm)

# 3. Initialize your application
app = EmailClassifier()  # or any other application

# 4. Prepare training data if optimizing
trainset = [
    dspy.Example(...).with_inputs(...),
    # more examples
]

# 5. Define metrics
def my_metric(gold, pred, trace=None):
    # Custom evaluation logic
    return score

# 6. Optimize if needed
optimizer = dspy.BootstrapFewShot(metric=my_metric)
optimized_app = optimizer.compile(app, trainset=trainset)

# 7. Deploy and use
result = optimized_app(your_input_data)
\`\`\`

### Performance Tips

1. **Caching**: Enable caching for development and testing
2. **Batch Processing**: Process multiple items together when possible
3. **Async Operations**: Use async versions for I/O-bound operations
4. **Model Selection**: Choose appropriate models for each task
5. **Monitoring**: Track performance metrics in production

### What's Next

Module 7 will cover:
- Production deployment strategies
- Scaling considerations
- Monitoring and maintenance
- Integration patterns

You now have comprehensive examples of real-world DSPy applications. Practice building and optimizing these systems for your specific use cases!`,
  exercises: [
    {
      id: 'dspy-m6-ex1',
      title: 'Build a Multi-Label Classifier',
      description:
        'Create a classifier that can assign multiple labels to text with confidence scores',
      difficulty: 'medium',
      type: 'code',
      instructions:
        'Build a multi-label classifier for news articles that can identify multiple relevant categories.',
      hints: [
        'Use structured output with lists',
        'Implement confidence scoring',
        'Handle threshold filtering',
      ],
      solution: `class NewsClassifier(dspy.Module):
    def __init__(self, categories: List[str]):
        super().__init__()
        self.categories = categories
        self.classifier = dspy.ChainOfThought(
            """article: str,
            categories: list[str] ->
            relevant_categories: list[str],
            confidence_scores: dict[str, float],
            primary_category: str,
            reasoning: str"""
        )
    
    def forward(self, article: str, threshold: float = 0.7):
        result = self.classifier(
            article=article,
            categories=self.categories
        )
        
        # Filter by confidence threshold
        filtered_categories = [
            cat for cat, score in result.confidence_scores.items()
            if score >= threshold
        ]
        
        return dspy.Prediction(
            categories=filtered_categories,
            primary=result.primary_category,
            scores=result.confidence_scores,
            reasoning=result.reasoning
        )

# Usage
categories = ["Technology", "Politics", "Sports", "Business", "Science"]
classifier = NewsClassifier(categories)
result = classifier(
    article="AI startup raises $50M to develop autonomous vehicles",
    threshold=0.6
)`,
      testCases: [
        {
          input: 'Multi-label news classifier with confidence scores',
          expected: true,
          description: 'Classifier correctly handles multiple labels and confidence filtering',
        },
      ],
    },
    {
      id: 'dspy-m6-ex2',
      title: 'Create a RAG QA System',
      description: 'Build a retrieval-augmented QA system with citation tracking',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Implement a RAG system that retrieves relevant passages and generates answers with citations.',
      hints: [
        'Generate multiple search queries',
        'Track passage sources',
        'Include confidence scoring',
      ],
      solution: `class RAGQA(dspy.Module):
    def __init__(self, retriever):
        super().__init__()
        self.retriever = retriever
        
        self.query_generator = dspy.ChainOfThought(
            "question -> queries: list[str]"
        )
        
        self.answerer = dspy.ChainOfThought(
            """question: str,
            passages: list[str] ->
            answer: str,
            citations: list[int],
            confidence: float"""
        )
    
    def forward(self, question: str, k: int = 5):
        # Generate search queries
        queries = self.query_generator(question=question).queries
        
        # Retrieve passages
        all_passages = []
        sources = []
        
        for i, query in enumerate(queries[:3]):
            results = self.retriever(query, k=k)
            for j, passage in enumerate(results.passages):
                all_passages.append(passage)
                sources.append({
                    'query': query,
                    'rank': j,
                    'id': len(all_passages) - 1
                })
        
        # Generate answer with citations
        result = self.answerer(
            question=question,
            passages=all_passages[:k*2]  # Use top passages
        )
        
        return dspy.Prediction(
            answer=result.answer,
            citations=result.citations,
            confidence=result.confidence,
            sources=[sources[c] for c in result.citations]
        )`,
      testCases: [
        {
          input: 'RAG QA system with citations',
          expected: true,
          description: 'System retrieves passages and tracks citations correctly',
        },
      ],
    },
    {
      id: 'dspy-m6-ex3',
      title: 'Build a Customer Service Agent',
      description: 'Create a ReAct agent for handling customer inquiries',
      difficulty: 'hard',
      type: 'code',
      instructions:
        'Implement an agent that can use tools to handle various customer service tasks.',
      hints: ['Define tool functions', 'Use ReAct for reasoning', 'Handle multiple query types'],
      solution: `class ServiceAgent(dspy.Module):
    def __init__(self):
        super().__init__()
        
        # Define tools
        self.tools = [
            self.lookup_order,
            self.check_inventory,
            self.process_return,
            self.escalate_issue
        ]
        
        # Initialize ReAct agent
        self.agent = dspy.ReAct(
            signature="query -> response",
            tools=self.tools,
            max_iters=5
        )
    
    def lookup_order(self, order_id: str) -> str:
        """Look up order status"""
        # Simulated lookup
        orders = {
            "12345": {"status": "Shipped", "eta": "2 days"},
            "67890": {"status": "Processing", "eta": "5 days"}
        }
        
        if order_id in orders:
            info = orders[order_id]
            return f"Order {order_id}: {info['status']}, ETA: {info['eta']}"
        return f"Order {order_id} not found"
    
    def check_inventory(self, product: str) -> str:
        """Check product availability"""
        inventory = {
            "laptop": 15,
            "phone": 42,
            "tablet": 0
        }
        
        count = inventory.get(product.lower(), -1)
        if count > 0:
            return f"{product} is in stock ({count} units)"
        elif count == 0:
            return f"{product} is out of stock"
        return f"Product '{product}' not found"
    
    def process_return(self, order_id: str, reason: str) -> str:
        """Process a return request"""
        return f"Return initiated for order {order_id}. Reason: {reason}. RMA number: RMA{order_id}"
    
    def escalate_issue(self, issue: str) -> str:
        """Escalate to human agent"""
        return f"Issue escalated to human agent. Ticket #ESC{hash(issue) % 10000}. Response within 24 hours."
    
    def forward(self, query: str):
        return self.agent(query=query)

# Usage
agent = ServiceAgent()
response = agent(query="I need to return order 12345 because it's damaged")`,
      testCases: [
        {
          input: 'Customer service agent with tools',
          expected: true,
          description: 'Agent correctly uses tools to handle customer queries',
        },
      ],
    },
    {
      id: 'dspy-m6-ex4',
      title: 'Create an Article Generator',
      description: 'Build a complete article writing system',
      difficulty: 'hard',
      type: 'code',
      instructions: 'Implement a system that can generate complete articles with proper structure.',
      hints: ['Create outline first', 'Write sections separately', 'Include intro and conclusion'],
      solution: `class ArticleGenerator(dspy.Module):
    def __init__(self):
        super().__init__()
        
        self.outliner = dspy.ChainOfThought(
            """topic: str,
            audience: str ->
            outline: list[str],
            key_points: list[str]"""
        )
        
        self.intro_writer = dspy.ChainOfThought(
            """topic: str,
            outline: list[str] ->
            introduction: str,
            hook: str"""
        )
        
        self.section_writer = dspy.ChainOfThought(
            """section_title: str,
            context: str ->
            content: str"""
        )
        
        self.conclusion_writer = dspy.ChainOfThought(
            """topic: str,
            key_points: list[str] ->
            conclusion: str,
            call_to_action: str"""
        )
    
    def forward(self, topic: str, audience: str = "general"):
        # Create outline
        outline_result = self.outliner(
            topic=topic,
            audience=audience
        )
        
        # Write introduction
        intro = self.intro_writer(
            topic=topic,
            outline=outline_result.outline
        )
        
        # Write sections
        sections = []
        context = intro.introduction
        
        for section_title in outline_result.outline:
            section = self.section_writer(
                section_title=section_title,
                context=context
            )
            sections.append({
                'title': section_title,
                'content': section.content
            })
            context += f"\\n{section.content}"
        
        # Write conclusion
        conclusion = self.conclusion_writer(
            topic=topic,
            key_points=outline_result.key_points
        )
        
        # Compile article
        article = f"{intro.hook}\\n\\n{intro.introduction}\\n\\n"
        
        for section in sections:
            article += f"## {section['title']}\\n\\n{section['content']}\\n\\n"
        
        article += f"{conclusion.conclusion}\\n\\n{conclusion.call_to_action}"
        
        return dspy.Prediction(
            article=article,
            outline=outline_result.outline,
            word_count=len(article.split()),
            sections=sections
        )`,
      testCases: [
        {
          input: 'Article generator with complete structure',
          expected: true,
          description: 'System generates well-structured articles with all components',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m6-quiz',
    title: 'Module 6: Building Real-World Applications - Knowledge Check',
    description: 'Test your understanding of building complete DSPy applications',
    passingScore: 5,
    questions: [
      {
        id: 'q1',
        question: 'What is a key benefit of using confidence scores in classification systems?',
        choices: [
          'They make the model faster',
          'They allow filtering results by reliability threshold',
          'They reduce token usage',
          'They improve accuracy automatically',
        ],
        correctAnswer: 1,
        explanation:
          'Confidence scores allow you to filter results based on reliability thresholds, enabling better control over prediction quality.',
      },
      {
        id: 'q2',
        question: 'In multi-hop question answering, what is the purpose of decomposing questions?',
        choices: [
          'To reduce complexity',
          'To break complex questions into simpler sub-questions that can be answered sequentially',
          'To save tokens',
          'To improve speed',
        ],
        correctAnswer: 1,
        explanation:
          'Decomposing questions allows the system to break down complex queries into simpler sub-questions that can be answered step by step.',
      },
      {
        id: 'q3',
        question: 'What is the main advantage of RAG (Retrieval-Augmented Generation)?',
        choices: [
          "It's faster than regular generation",
          'It uses less memory',
          'It grounds answers in retrieved factual information',
          'It requires no training',
        ],
        correctAnswer: 2,
        explanation:
          'RAG grounds answers in retrieved factual information, reducing hallucinations and improving accuracy.',
      },
      {
        id: 'q4',
        question: 'What makes ReAct agents different from simple chain-of-thought modules?',
        choices: [
          'They are faster',
          'They can reason about actions and use tools iteratively',
          'They use less tokens',
          "They don't need prompting",
        ],
        correctAnswer: 1,
        explanation:
          'ReAct agents can reason about actions and use tools iteratively, allowing them to solve complex tasks through tool usage.',
      },
      {
        id: 'q5',
        question: 'Why is memory important in conversational agents?',
        choices: [
          'To reduce API costs',
          'To maintain context and personalization across interactions',
          'To improve response speed',
          'To simplify implementation',
        ],
        correctAnswer: 1,
        explanation:
          'Memory allows agents to maintain context and personalization across interactions, enabling more coherent conversations.',
      },
      {
        id: 'q6',
        question: 'What is a best practice when building content generation systems?',
        choices: [
          'Generate everything in one pass',
          'Use the largest model available',
          'Break generation into structured components (outline, sections, conclusion)',
          'Avoid using citations',
        ],
        correctAnswer: 2,
        explanation:
          'Breaking generation into structured components ensures better organization and allows for more control over the output.',
      },
      {
        id: 'q7',
        question: 'How can you optimize a classification system in DSPy?',
        choices: [
          'Only by using larger models',
          'By manually writing better prompts',
          'Using teleprompters like BootstrapFewShot with training examples',
          'By reducing the number of classes',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy teleprompters like BootstrapFewShot can optimize classifiers using training examples and metrics.',
      },
    ],
  },
};
